// src/pages/dashboard/Overview.tsx
import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Users,
  CreditCard,
  TrendingUp,
  BellRing,
  Loader2,
  ArrowUpRight,
  ShieldCheck,
} from "lucide-react";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { useAppSelector } from "../../app/hooks";
import { selectCurrentUser } from "../../app/features/authSlice";
import {
  useGetAdminStatsQuery,
  useGetRevenueSeriesQuery,
} from "../../app/api/adminApi";

function cn(...c: Array<string | false | null | undefined>) {
  return c.filter(Boolean).join(" ");
}
const naira = (n?: number) => `₦${Number(n || 0).toLocaleString()}`;
const fmtDate = (d?: string) =>
  d
    ? new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
      }).format(new Date(d))
    : "—";

const StatCard: React.FC<{
  label: string;
  value: string | number;
  sub?: string;
  icon: React.ReactNode;
  accent: string;
  to?: string;
}> = ({ label, value, sub, icon, accent, to }) => {
  const body = (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:shadow-md">
      <div className="flex items-start justify-between">
        <div
          className={cn(
            "flex h-10 w-10 items-center justify-center rounded-xl",
            accent
          )}
        >
          {icon}
        </div>
        {to && <ArrowUpRight className="h-4 w-4 text-slate-300" />}
      </div>
      <p className="mt-4 text-2xl font-bold tracking-tight text-slate-900">
        {value}
      </p>
      <p className="text-sm font-medium text-slate-500">{label}</p>
      {sub && <p className="mt-1 text-xs text-slate-400">{sub}</p>}
    </div>
  );
  return to ? <Link to={to}>{body}</Link> : body;
};

const Overview: React.FC = () => {
  const user = useAppSelector(selectCurrentUser);
  const [days, setDays] = useState(30);

  const { data: statsRes, isLoading } = useGetAdminStatsQuery();
  const { data: seriesRes } = useGetRevenueSeriesQuery({ days });

  const stats = statsRes?.data;
  const series = seriesRes?.data || [];

  const leadTypes = stats
    ? [
        { name: "Leads", value: stats.leads.byType.lead },
        { name: "Funnels", value: stats.leads.byType.custom_funnel },
        { name: "Faith", value: stats.leads.byType.faith_that_works },
        { name: "Events", value: stats.leads.byType.monetize_knowledge },
        { name: "Wealth", value: stats.leads.byType.wealth_method },
      ]
    : [];

  if (isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center text-slate-500">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div>
      {/* header */}
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">
            Overview
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            {user ? `Welcome back, ${user.firstName}.` : "Welcome back."} Here's
            what's happening across the platform.
          </p>
        </div>
        <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1 shadow-sm">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={cn(
                "rounded-lg px-3 py-1.5 text-xs font-semibold transition",
                days === d
                  ? "bg-slate-900 text-white"
                  : "text-slate-500 hover:text-slate-800"
              )}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {/* stat cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Paid revenue"
          value={naira(stats?.revenue.paid)}
          sub={`${stats?.orders.paid ?? 0} paid orders`}
          icon={<TrendingUp className="h-5 w-5 text-emerald-600" />}
          accent="bg-emerald-50"
          to="/user/payments"
        />
        <StatCard
          label="Total contacts"
          value={(stats?.leads.total ?? 0).toLocaleString()}
          sub="Across all funnels"
          icon={<Users className="h-5 w-5 text-violet-600" />}
          accent="bg-violet-50"
          to="/user/contacts"
        />
        <StatCard
          label="Orders"
          value={(stats?.orders.total ?? 0).toLocaleString()}
          sub={`${stats?.orders.byStatus?.pending ?? 0} pending`}
          icon={<CreditCard className="h-5 w-5 text-sky-600" />}
          accent="bg-sky-50"
          to="/user/payments"
        />
        <StatCard
          label="Open follow-ups"
          value={stats?.followUps.pending ?? 0}
          sub={`${stats?.followUps.overdue ?? 0} overdue`}
          icon={<BellRing className="h-5 w-5 text-amber-600" />}
          accent="bg-amber-50"
          to="/user/follow-ups"
        />
      </div>

      {/* charts */}
      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm lg:col-span-2">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-900">
              Revenue &amp; signups
            </h2>
            <span className="text-xs text-slate-400">Last {days} days</span>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={series}>
              <defs>
                <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis
                dataKey="date"
                tickFormatter={fmtDate}
                tick={{ fontSize: 11, fill: "#94a3b8" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 11, fill: "#94a3b8" }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                formatter={(v: any, name: any) =>
                  name === "revenue" ? naira(v) : v
                }
                labelFormatter={fmtDate}
              />
              <Area
                type="monotone"
                dataKey="revenue"
                stroke="#10b981"
                strokeWidth={2}
                fill="url(#rev)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="mb-4 text-sm font-semibold text-slate-900">
            Contacts by source
          </h2>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={leadTypes}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 11, fill: "#94a3b8" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 11, fill: "#94a3b8" }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip />
              <Bar dataKey="value" fill="#8b5cf6" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* recent orders */}
      <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-900">Recent orders</h2>
          <Link
            to="/user/payments"
            className="text-xs font-semibold text-violet-600 hover:text-violet-700"
          >
            View all
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-slate-100 text-xs uppercase tracking-wide text-slate-400">
                <th className="pb-2 font-semibold">Customer</th>
                <th className="pb-2 font-semibold">Amount</th>
                <th className="pb-2 font-semibold">Status</th>
                <th className="pb-2 font-semibold">Date</th>
              </tr>
            </thead>
            <tbody>
              {(stats?.recentOrders || []).length ? (
                stats!.recentOrders.map((o) => (
                  <tr
                    key={o._id}
                    className="border-b border-slate-50 last:border-0"
                  >
                    <td className="py-2.5">
                      <p className="font-medium text-slate-800">
                        {o.name || "—"}
                      </p>
                      <p className="text-xs text-slate-400">{o.email}</p>
                    </td>
                    <td className="py-2.5 font-semibold text-slate-800">
                      {naira(o.amount)}
                    </td>
                    <td className="py-2.5">
                      <span
                        className={cn(
                          "rounded-full border px-2 py-0.5 text-xs font-semibold",
                          o.status === "paid"
                            ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                            : o.status === "pending"
                              ? "border-amber-200 bg-amber-50 text-amber-700"
                              : "border-slate-200 bg-slate-50 text-slate-500"
                        )}
                      >
                        {o.status}
                      </span>
                    </td>
                    <td className="py-2.5 text-xs text-slate-500">
                      {fmtDate(o.createdAt)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="py-8 text-center text-slate-400">
                    No orders yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* admin footer note */}
      <div className="mt-6 flex items-center gap-2 text-xs text-slate-400">
        <ShieldCheck className="h-3.5 w-3.5" />
        Admin dashboard · signed in as {user?.email}
      </div>
    </div>
  );
};

export default Overview;
